import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useVoiceInputWhisper, type WhisperOwnerGuard } from "@/hooks/useVoiceInputWhisper";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import type { ExtractedAgendaEvent } from "@/hooks/useAgendaVoiceCapture";
import { agendaReminderOffsets } from "@/lib/agendaCaptureData";
import { mirrorAuthIdentity, sameMirrorAuthIdentity, type MirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { shortFor, type Lang } from "@/i18n/locale";

export type { ExtractedAgendaEvent };
export type TaskCaptureStatus = "idle" | "starting" | "recording" | "transcribing" | "extracting" | "ready" | "error";
export type ExtractedTask = {
  title: string;
  due_date: string | null;
  priority: "Critical" | "Important" | "Normal";
};
export type TaskCaptureResult =
  | { kind: "task"; data: ExtractedTask }
  | { kind: "event"; events: ExtractedAgendaEvent[]; transcript: string };
export interface UseTaskVoiceCaptureReturn {
  status: TaskCaptureStatus;
  recordingSeconds: number;
  amplitude: number;
  result: TaskCaptureResult | null;
  error: string | null;
  lastTranscript: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
  supported: boolean;
}
interface Capture {
  generation: number;
  guard: WhisperOwnerGuard | null;
  identity: MirrorAuthIdentity | null;
  started: boolean;
  stopping: boolean;
  controller: AbortController;
}
interface CaptureState {
  generation: number;
  status: TaskCaptureStatus;
  result: TaskCaptureResult | null;
  error: string | null;
  lastTranscript: string | null;
}
const idle = (generation: number): CaptureState => ({
  generation, status: "idle", result: null, error: null, lastTranscript: null,
});
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** One owner/session-bound attempt: recording -> STT -> extraction -> review. */
export function useTaskVoiceCapture(lang: Lang, ownerId?: string): UseTaskVoiceCaptureReturn {
  const stt = useVoiceInputWhisper(lang);
  const surfaceQuota = useQuotaErrorSurface();
  const mounted = useRef(false), generation = useRef(0), expectedOwner = useRef(ownerId);
  const identity = useRef<MirrorAuthIdentity | null | undefined>(undefined);
  const capture = useRef<Capture | null>(null);
  const cancelRecorder = useRef(stt.cancelListening); cancelRecorder.current = stt.cancelListening;
  const [state, setState] = useState<CaptureState>(() => idle(0));
  // Revoke synchronously: a render for another account must not expose an old draft.
  if (expectedOwner.current !== ownerId) {
    expectedOwner.current = ownerId;
    generation.current++;
  }
  const current = useCallback((attempt: Capture) =>
    mounted.current && capture.current === attempt && attempt.generation === generation.current &&
    (!attempt.identity || sameMirrorAuthIdentity(identity.current ?? null, attempt.identity)) &&
    (!attempt.identity || !expectedOwner.current || expectedOwner.current === attempt.identity.ownerId), []);
  const assert = useCallback((attempt: Capture) => {
    if (!current(attempt)) throw new Error("invalid_session");
  }, [current]);
  const release = useCallback(() => {
    const old = capture.current; capture.current = null;
    if (old) { old.controller.abort(); if (old.guard) cancelRecorder.current(old.guard); }
  }, []);
  const reset = useCallback(() => {
    generation.current++; release();
    if (mounted.current) setState(idle(generation.current));
  }, [release]);
  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = mirrorAuthIdentity(session), previous = identity.current;
      identity.current = next;
      // The initial valid observation may finish bootstrap; it does not revoke
      // an attempt already waiting for the same authenticated identity.
      if (previous === undefined && next && (!expectedOwner.current || next.ownerId === expectedOwner.current)) return;
      if (!sameMirrorAuthIdentity(previous ?? null, next)) reset();
    });
    const epoch = generation;
    return () => { mounted.current = false; epoch.current++; release(); subscription.unsubscribe(); };
  }, [release, reset]);
  useEffect(() => { reset(); }, [ownerId, reset]);

  const ownedSession = useCallback(async (attempt: Capture) => {
    assert(attempt);
    const user = await supabase.auth.getUser(); assert(attempt);
    const uid = user.data.user?.id;
    if (user.error || !uid || expectedOwner.current && expectedOwner.current !== uid) throw new Error("invalid_session");
    const result = await supabase.auth.getSession(); assert(attempt);
    const next = mirrorAuthIdentity(result.data.session);
    if (result.error || !next || next.ownerId !== uid ||
      identity.current !== undefined && !sameMirrorAuthIdentity(identity.current, next) ||
      attempt.identity && !sameMirrorAuthIdentity(attempt.identity, next)) throw new Error("invalid_session");
    if (identity.current === undefined) identity.current = next;
    return next;
  }, [assert]);
  const publish = useCallback((attempt: Capture, patch: Partial<CaptureState>) => {
    if (!current(attempt)) return;
    setState(prev => ({ ...prev, ...patch, generation: attempt.generation }));
  }, [current]);

  const start = useCallback(async () => {
    if (!mounted.current || capture.current && current(capture.current)) return;
    release(); generation.current++;
    const attempt: Capture = {
      generation: generation.current, guard: null, identity: null, started: false,
      stopping: false, controller: new AbortController(),
    };
    capture.current = attempt;
    setState({ ...idle(attempt.generation), status: "starting" });
    try {
      const actor = await ownedSession(attempt); assert(attempt); attempt.identity = actor;
      const guard: WhisperOwnerGuard = {
        ownerId: actor.ownerId, authSessionId: actor.authSessionId, isCurrent: () => current(attempt),
      };
      attempt.guard = guard;
      const started = await stt.startListening(guard); assert(attempt);
      if (!started) throw new Error("microphone_unavailable");
      attempt.started = true; publish(attempt, { status: "recording" });
    } catch (error) {
      if (!current(attempt)) return;
      const busy = error instanceof Error && error.message === "native_audio_session_busy";
      const code = busy ? "native_audio_session_busy" : error instanceof Error && error.message === "invalid_session" ? "invalid_session" : "recording_failed";
      (busy ? console.warn : console.error)("[useTaskVoiceCapture] capture failed", { stage: "recording", code });
      publish(attempt, { status: "error", error: error instanceof Error ? error.message : "mic error" });
      release();
    }
  }, [current, release, ownedSession, assert, stt, publish]);

  const stop = useCallback(async () => {
    const attempt = capture.current;
    if (!attempt || !current(attempt) || !attempt.started || attempt.stopping || !attempt.guard || !attempt.identity) return;
    // Consume synchronously; two clicks before React commits cannot extract twice.
    attempt.stopping = true;
    let stage = "transcription";
    try {
      publish(attempt, { status: "transcribing" });
      const transcript = (await stt.stopListening(attempt.guard)).trim(); assert(attempt);
      if (!transcript) throw new Error("empty transcript");
      publish(attempt, { status: "extracting", lastTranscript: transcript });
      await ownedSession(attempt); assert(attempt);
      const language = shortFor(lang) ?? "en";
      const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC";
      const userTz = deviceZone === "UTC" ? "Etc/UTC" : deviceZone;
      const currentDateIso = new Intl.DateTimeFormat("sv-SE", { timeZone: userTz }).format(new Date());
      const options = { headers: { Authorization: "Bearer " + attempt.identity.accessToken }, signal: attempt.controller.signal };
      stage = "task_extraction";
      const { data: taskData, error: taskErr } = await supabase.functions.invoke("task-extract", {
        ...options, body: { transcript, language, current_date_iso: currentDateIso },
      }); assert(attempt);
      if (taskErr) {
        if (await surfaceQuota(taskErr)) { assert(attempt); publish(attempt, { status: "idle" }); return; }
        assert(attempt); throw taskErr;
      }
      if (object(taskData) && taskData.kind_mismatch === "event_detected") {
        await ownedSession(attempt); assert(attempt);
        stage = "event_extraction";
        const { data: eventData, error: eventErr } = await supabase.functions.invoke("agenda-extract", {
          ...options, body: { transcript, language, user_timezone: userTz, reference_date_iso: new Date().toISOString() },
        }); assert(attempt);
        if (eventErr) {
          if (await surfaceQuota(eventErr)) { assert(attempt); publish(attempt, { status: "idle" }); return; }
          assert(attempt); throw eventErr;
        }
        if (!object(eventData) || !Array.isArray(eventData.events) || eventData.events.length > 1) throw new Error("invalid agenda payload");
        const events = eventData.events as ExtractedAgendaEvent[];
        for (const event of events) {
          if (!object(event) || typeof event.title !== "string" || typeof event.start_iso !== "string" ||
            !Array.isArray(event.attendees) || event.attendees.some(value => typeof value !== "string")) throw new Error("invalid agenda payload");
          event.reminders = agendaReminderOffsets(event.reminders, event.reminder_minutes);
        }
        await ownedSession(attempt); assert(attempt);
        publish(attempt, { result: { kind: "event", events, transcript }, status: "ready" });
        return;
      }
      if (!object(taskData) || typeof taskData.title !== "string" ||
        taskData.due_date !== null && typeof taskData.due_date !== "string" ||
        !["Critical", "Important", "Normal"].includes(String(taskData.priority))) throw new Error("invalid task payload");
      await ownedSession(attempt); assert(attempt);
      publish(attempt, { result: { kind: "task", data: taskData as ExtractedTask }, status: "ready" });
    } catch (error) {
      if (current(attempt)) {
        const code = error instanceof Error && error.message === "empty transcript" ? "empty_transcript" : error instanceof Error && error.message === "invalid_session" ? "invalid_session" : error instanceof Error && ["invalid task payload", "invalid agenda payload"].includes(error.message) ? "invalid_payload" : "extraction_failed";
        console.error("[useTaskVoiceCapture] capture failed", { stage, code });
        publish(attempt, { error: error instanceof Error ? error.message : "capture failed", status: "error" });
      }
    } finally {
      if (capture.current === attempt) capture.current = null;
    }
  }, [current, publish, stt, assert, ownedSession, lang, surfaceQuota]);

  const visible = state.generation === generation.current ? state : idle(generation.current);
  return { status: visible.status, recordingSeconds: stt.recordingSeconds, amplitude: stt.amplitude,
    result: visible.result, error: visible.error, lastTranscript: visible.lastTranscript,
    start, stop, reset, supported: stt.supported };
}
